import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../../assets/logo.png";
import logoLight from "../../assets/logo-pos.png";
import { environments } from "../../environments";
import { useCheckPermissaoSegura } from "../../hooks/useCheckPermissaoSegura";
import { SinterAuditIntegracao } from "../../interfaces";
import { ColorModeContext } from "../../providers/Theme";
import { getSinterAuditoria, getTenantConfig, retrySinterIntegracao } from "../../services/api";
import { getMunicipio } from "../../services/data";
import { hasIntegracaoSinter } from "../../utils/integracaoSinter";
import { getSocket } from "../../services/socket";
import { formatarDataBrasilia } from "../../utils/formatDate";
import {
  formatSinterDetalhes,
  getSinterDataHora,
  getSinterImobiliarioId,
  getSinterTipoRegistro,
  matchesSinterSearch,
} from "../../utils/sinterAuditoria";
import { DialogVersion } from "../Dashboard/DialogVersion";
import LoadingPage from "../LoadingPage";
import SinterDetalhesCell from "./SinterDetalhesCell";
import pj from "../../../package.json";

type StatusFilter = "todos" | "sucesso" | "erro";

const SINTER_AUTO_REFRESH_MS = 60_000;
const SINTER_AUTO_REFRESH_LABEL = "1 minuto";
const SINTER_ACTION_BAR_HEIGHT = 50;

const sinterRefreshQueryOptions = {
  refetchInterval: SINTER_AUTO_REFRESH_MS,
  refetchIntervalInBackground: false,
};

function formatHorarioUltimaAtualizacao(timestampMs: number): string {
  if (!timestampMs) return "—";
  return new Date(timestampMs).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function AutoRefreshStatus(props: {
  isFetching: boolean;
  ultimaAtualizacaoMs: number;
  intervalLabel: string;
  onRefresh: () => void;
  disabled?: boolean;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        height: SINTER_ACTION_BAR_HEIGHT,
        boxSizing: "border-box",
        pl: 1.5,
        pr: 0.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.5 : 0.85),
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
          bgcolor: props.isFetching ? theme.palette.warning.main : theme.palette.success.main,
          animation: props.isFetching
            ? "sinterDotPulseActive 1s ease-in-out infinite"
            : "sinterDotPulseGreen 2s ease-in-out infinite",
          "@keyframes sinterDotPulseGreen": {
            "0%, 100%": {
              opacity: 1,
              transform: "scale(1)",
              boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.45)}`,
            },
            "50%": {
              opacity: 0.75,
              transform: "scale(1.15)",
              boxShadow: `0 0 0 7px ${alpha(theme.palette.success.main, 0)}`,
            },
          },
          "@keyframes sinterDotPulseActive": {
            "0%, 100%": {
              opacity: 1,
              transform: "scale(1)",
              boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.45)}`,
            },
            "50%": {
              opacity: 0.7,
              transform: "scale(1.15)",
              boxShadow: `0 0 0 7px ${alpha(theme.palette.warning.main, 0)}`,
            },
          },
        }}
      />
      <Tooltip
        title={`Atualização automática a cada ${props.intervalLabel}`}
        arrow
        placement="bottom"
      >
        <Box sx={{ minWidth: 0, flex: 1, py: 0.25 }}>
          <Typography variant="caption" fontWeight={600} lineHeight={1.25} noWrap display="block">
            Atualização automática
          </Typography>
          <Typography variant="caption" color="text.secondary" lineHeight={1.25} noWrap display="block">
            A cada {props.intervalLabel} · {formatHorarioUltimaAtualizacao(props.ultimaAtualizacaoMs)}
          </Typography>
        </Box>
      </Tooltip>
      <Box
        sx={{
          width: "1px",
          alignSelf: "stretch",
          my: 1,
          bgcolor: alpha(theme.palette.divider, 0.2),
          flexShrink: 0,
        }}
      />
      <Tooltip title="Atualizar agora" arrow placement="bottom">
        <span>
          <IconButton
            size="small"
            onClick={props.onRefresh}
            disabled={props.disabled || props.isFetching}
            sx={{ flexShrink: 0 }}
          >
            {props.isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

async function fetchSinterStats() {
  const first = await getSinterAuditoria(1, 100);
  if (!first) return { total: 0, sucesso: 0, erro: 0 };

  let sucesso = 0;
  let erro = 0;

  const count = (rows: SinterAuditIntegracao[]) => {
    rows.forEach((row) => {
      if (row.processado) sucesso += 1;
      else erro += 1;
    });
  };

  count(first.results);

  if (first.total_pages > 1) {
    const pages = Array.from({ length: first.total_pages - 1 }, (_, i) => i + 2);
    const responses = await Promise.all(pages.map((p) => getSinterAuditoria(p, 100)));
    responses.forEach((res) => res && count(res.results));
  }

  return { total: first.total_records, sucesso, erro };
}

function StatCard(props: {
  label: string;
  value: number;
  icon: ReactNode;
  accent?: "default" | "success" | "error";
}) {
  const theme = useTheme();
  const color =
    props.accent === "success"
      ? theme.palette.success.main
      : props.accent === "error"
        ? theme.palette.error.main
        : theme.palette.text.primary;

  const iconColor =
    props.accent === "success"
      ? theme.palette.success.main
      : props.accent === "error"
        ? theme.palette.error.main
        : theme.palette.text.secondary;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 140,
        px: 2.5,
        py: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.4 : 0.8),
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.3 }}>
            {props.label}
          </Typography>
          <Typography variant="h4" fontWeight={600} sx={{ mt: 0.5, color, lineHeight: 1.2 }}>
            {props.value}
          </Typography>
        </Box>
        <Box sx={{ color: iconColor, opacity: 0.85, mt: 0.25 }}>{props.icon}</Box>
      </Box>
    </Box>
  );
}

export default function SinterAuditoriaPage() {
  const autorizado = useCheckPermissaoSegura();
  const theme = useTheme();
  const color = useContext(ColorModeContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const municipio = getMunicipio();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("todos");
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  useEffect(() => {
    if (!searchParams.has("municipio")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("municipio");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: integracaoDisponivel, isLoading: loadingIntegracao } = useQuery({
    queryKey: ["sinter-integracao-disponivel", municipio],
    queryFn: async () => {
      const config = await getTenantConfig();
      return hasIntegracaoSinter(config);
    },
  });

  const {
    data: stats,
    isLoading: loadingStats,
    refetch: refetchStats,
    dataUpdatedAt: statsUpdatedAt,
    isFetching: isFetchingStats,
  } = useQuery({
    queryKey: ["sinter-auditoria-stats", municipio],
    queryFn: fetchSinterStats,
    enabled: integracaoDisponivel === true,
    ...sinterRefreshQueryOptions,
  });

  const {
    data: auditoria,
    isLoading: loadingTable,
    refetch: refetchTable,
    isFetching: isFetchingTable,
    dataUpdatedAt: tableUpdatedAt,
  } = useQuery({
    queryKey: ["sinter-auditoria", municipio, page, rowsPerPage],
    queryFn: () => getSinterAuditoria(page + 1, rowsPerPage),
    enabled: integracaoDisponivel === true,
    ...sinterRefreshQueryOptions,
  });

  const isFetching = isFetchingStats || isFetchingTable;
  const ultimaAtualizacaoMs = Math.max(statsUpdatedAt, tableUpdatedAt);

  const filteredRows = useMemo(() => {
    const rows = auditoria?.results ?? [];

    return rows.filter((row) => {
      if (statusFilter === "sucesso" && !row.processado) return false;
      if (statusFilter === "erro" && row.processado) return false;
      return matchesSinterSearch(row, search);
    });
  }, [auditoria?.results, search, statusFilter]);

  const surface = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.35 : 0.9),
    overflow: "hidden",
  };

  function handleVoltarDashboard() {
    const loaded = localStorage.getItem("loaded");
    const idLote = loaded ? JSON.parse(loaded) : null;
    if (idLote) {
      navigate(`/dashboard/${idLote}`);
      return;
    }
    navigate("/");
  }

  async function handleRetry(record: SinterAuditIntegracao) {
    setRetryingId(record.id_audit_integracao);

    try {
      const retriedViaApi = await retrySinterIntegracao(record.id_audit_integracao);

      if (!retriedViaApi) {
        const imobiliarioId = getSinterImobiliarioId(record);
        const municipio = getMunicipio();
        const socket = getSocket({ enableSinter: true });

        if (!imobiliarioId || !socket?.connected) {
          toast.error("Não foi possível reprocessar este registro.");
          return;
        }

        socket.emit("startWebhook", { id: imobiliarioId, municipio, camada: "imobiliario", method: "PUT" });
      }

      toast.success("Nova tentativa de integração iniciada.");
      await Promise.all([refetchTable(), refetchStats(), queryClient.invalidateQueries({ queryKey: ["sinter-auditoria"] })]);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao tentar novamente a integração.");
    } finally {
      setRetryingId(null);
    }
  }

  async function handleAtualizar() {
    await Promise.all([refetchTable(), refetchStats()]);
    toast.success("Lista atualizada.");
  }

  if (!autorizado) {
    return <LoadingPage message="Verificando permissões de acesso" />;
  }

  if (loadingIntegracao) {
    return <LoadingPage message="Verificando integração SINTER" />;
  }

  if (integracaoDisponivel === false) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: theme.palette.mode === "dark" ? "#0d0f12" : "#f4f6f8",
          px: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 420,
            width: "100%",
            p: 3,
            borderRadius: 3,
            textAlign: "center",
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.4 : 0.95),
          }}
        >
          <BlockOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Auditoria SINTER indisponível
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            A integração SINTER não está habilitada para o município atual.
          </Typography>
          <Button variant="contained" onClick={handleVoltarDashboard} sx={{ borderRadius: 2, textTransform: "none" }}>
            Voltar ao dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  if (loadingStats && !stats) {
    return <LoadingPage message="Carregando auditoria SINTER" />;
  }

  const filters: { value: StatusFilter; label: string; icon: ReactNode }[] = [
    { value: "todos", label: "Todos", icon: <FilterListIcon fontSize="small" /> },
    { value: "sucesso", label: "Sucesso", icon: <CheckCircleOutlineIcon fontSize="small" color="success" /> },
    { value: "erro", label: "Erro", icon: <ErrorOutlineIcon fontSize="small" color="error" /> },
  ];

  const filterAtivo = filters.find((f) => f.value === statusFilter);
  const filterMenuAberto = Boolean(filterAnchorEl);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.mode === "dark" ? "#0d0f12" : "#f4f6f8",
        pb: 6,
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          backdropFilter: "blur(8px)",
        }}
      >
        <Box
          sx={{
            maxWidth: 1100,
            mx: "auto",
            px: { xs: 2, sm: 3 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <img
              className="logo-dashboard"
              src={theme.palette.mode === "dark" ? logo : logoLight}
              alt="logo"
              style={{ height: 32 }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Caracterização
              </Typography>
              <DialogVersion>
                <Typography variant="caption" color="text.secondary">
                  {pj.version} · {environments.ambiente}
                </Typography>
              </DialogVersion>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Voltar">
              <IconButton size="small" onClick={handleVoltarDashboard}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={color.toggleColorMode}>
              {theme.palette.mode === "dark" ? <Brightness7Icon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, sm: 3 }, pt: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "flex-end" }}
          gap={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
              Integração
            </Typography>
            <Typography variant="h5" fontWeight={600} sx={{ mt: 0.25 }}>
              Auditoria SINTER
            </Typography>
          </Box>

          <AutoRefreshStatus
            isFetching={isFetching}
            ultimaAtualizacaoMs={ultimaAtualizacaoMs}
            intervalLabel={SINTER_AUTO_REFRESH_LABEL}
            onRefresh={handleAtualizar}
            disabled={loadingTable || loadingStats}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} sx={{ mb: 3 }}>
          <StatCard
            label="Total"
            value={stats?.total ?? 0}
            icon={<StorageOutlinedIcon />}
          />
          <StatCard
            label="Sucesso"
            value={stats?.sucesso ?? 0}
            accent="success"
            icon={<CheckCircleOutlineIcon />}
          />
          <StatCard
            label="Erro"
            value={stats?.erro ?? 0}
            accent="error"
            icon={<ErrorOutlineIcon />}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por imóvel, tipo ou detalhes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title={`Filtro: ${filterAtivo?.label ?? "Todos"}`}>
            <IconButton
              size="small"
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              sx={{
                flexShrink: 0,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                color: statusFilter !== "todos" ? theme.palette.primary.main : "inherit",
              }}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={filterAnchorEl}
            open={filterMenuAberto}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: { minWidth: 160, borderRadius: 2, mt: 0.5 },
            }}
          >
            {filters.map((f) => (
              <MenuItem
                key={f.value}
                selected={statusFilter === f.value}
                onClick={() => {
                  setStatusFilter(f.value);
                  setFilterAnchorEl(null);
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>{f.icon}</ListItemIcon>
                <ListItemText primary={f.label} />
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        <Box sx={surface}>
          {loadingTable ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {["Data/Hora", "Id do Imóvel", "Tipo", "Status", "Detalhes", ""].map((col) => (
                        <TableCell
                          key={col || "actions"}
                          align={col === "" ? "right" : "left"}
                          sx={{
                            py: 1.5,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                            color: "text.secondary",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            letterSpacing: 0.2,
                          }}
                        >
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                          <Typography variant="body2" color="text.secondary">
                            Nenhum registro encontrado.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRows.map((row) => {
                        const dataHora = getSinterDataHora(row);
                        const imobiliarioId = getSinterImobiliarioId(row);
                        const detalhes = formatSinterDetalhes(row);
                        const isRetrying = retryingId === row.id_audit_integracao;

                        return (
                          <TableRow
                            key={row.id_audit_integracao}
                            sx={{
                              "&:last-child td": { border: 0 },
                              "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.04) },
                            }}
                          >
                            <TableCell sx={{ py: 2, whiteSpace: "nowrap", fontSize: "0.875rem" }}>
                              {dataHora ? formatarDataBrasilia(dataHora) : "—"}
                            </TableCell>
                            <TableCell sx={{ py: 2, fontFamily: "monospace", fontSize: "0.875rem" }}>
                              {imobiliarioId ?? "—"}
                            </TableCell>
                            <TableCell sx={{ py: 2, fontSize: "0.875rem" }}>{getSinterTipoRegistro(row)}</TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Chip
                                size="small"
                                label={row.processado ? "Sucesso" : "Erro"}
                                icon={row.processado ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                                color={row.processado ? "success" : "error"}
                                variant={row.processado ? "outlined" : "filled"}
                                sx={{ fontWeight: 500, borderRadius: 1.5 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2, verticalAlign: "top" }}>
                              <SinterDetalhesCell detalhes={detalhes} />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              {!row.processado && (
                                <Tooltip title="Tentar novamente">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={isRetrying}
                                      onClick={() => handleRetry(row)}
                                      sx={{ color: "text.secondary" }}
                                    >
                                      {isRetrying ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={auditoria?.total_records ?? 0}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Por página"
                sx={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
              />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
